import Button from '../button/button';
import ActiveFlag from '../../assets/red_flag.png';
import InactiveFlag from '../../assets/white_flag.png';
import { Trans } from 'react-i18next';
import { PlayerUpdate } from '@inplay/models';
import TimePicker from '../timePicker/timePicker';
import moment from 'moment';
import { isUndefined } from 'lodash';

/**
 * Renders a section for entering player details including identifying information
 * and a time range (from - until) using custom `TimePicker` components.
 *
 * @param playerSummary - The current player data object, containing a `time` field with `from` and `until` strings.
 * @param setPlayerSummary - React state setter to update the player data when time is changed.
 *
 * Components:
 * - An input field for identifying information (currently not bound to state).
 * - Two `TimePicker` components for selecting 'From' and 'Until' time values.
 *   - The 'From' time cannot be after the 'Until' time.
 *   - The 'Until' time cannot be before the 'From' time.
 *
 * Styling:
 * - Uses Tailwind CSS for spacing, focus rings, and background colors.
 */
export const renderPlayerDetails = (
  playerSummary: PlayerUpdate,
  setPlayerSummary: (player: PlayerUpdate) => void,
  descriptionPlaceHolder: string,
  venueBranchOpeningTime?: Date,
  sweepTime?: Date,
  venueBranchClosingTime?: Date,
) => {
  return (
    <div>
      <input
        type="text"
        placeholder={descriptionPlaceHolder}
        value={playerSummary.description || ''}
        onChange={(e) =>
          setPlayerSummary({ ...playerSummary, description: e.target.value })
        }
        className="bg-[#F7F7F7] rounded-full px-4 py-2 text-sm w-full mb-2 focus:outline-none focus:ring-2 focus:ring-[#2F41A5]"
      />

      <div className="flex items-center space-x-2">
        <TimePicker
          timeBreak={60}
          selectedTime={
            (playerSummary.startTime &&
              moment.unix(playerSummary.startTime).format('HH:mm')) ||
            ''
          }
          onTimeChange={(startTime: string) => {
            setPlayerSummary({
              ...playerSummary,
              startTime: moment(startTime, 'HH:mm').unix(),
            });
          }}
          minTime={moment(venueBranchOpeningTime).format('hh:mm A')}
          maxTime={moment(sweepTime).format('hh:mm A')}
          placeholder={<Trans i18nKey={'sweeps.selectPlayers.from'} />}
        />

        <span>-</span>

        <TimePicker
          timeBreak={60}
          selectedTime={
            (playerSummary.endTime &&
              moment.unix(playerSummary.endTime).format('HH:mm')) ||
            ''
          }
          onTimeChange={(endTime: string) => {
            setPlayerSummary({
              ...playerSummary,
              endTime: moment(endTime, 'HH:mm').unix(),
            });
          }}
          minTime={moment(sweepTime).format('hh:mm A')}
          maxTime={moment(venueBranchClosingTime).format('hh:mm A')}
          placeholder={<Trans i18nKey={'sweeps.selectPlayers.until'} />}
        />
      </div>
    </div>
  );
};

/**
 * Renders a toggle button group with "Yes" and "No" options.
 * The selected value is updated in the parent state using `setPlayerSummary`.
 *
 * @param value - Current boolean value (true for Yes, false for No).
 * @param setPlayerSummary - Setter function to update player data state.
 * @param key - The key in the player data object to update with the toggle value.
 * @param activeYesClass - Tailwind classes to apply when "Yes" is active.
 * @param activeNoClass - Tailwind classes to apply when "No" is active.
 *
 * @returns JSX element containing two toggle buttons.
 *
 * @example
 * renderToggleButton(playerData.isGambler, setPlayerSummary, 'isGambler', 'bg-green-600 text-white', 'bg-red-600 text-white')
 */
export const renderToggleButton = (
  value: boolean,
  setPlayerSummary: ((newValue: boolean) => void) | null,
  activeYesClass: string,
  activeNoClass: string,
  disabled: boolean = false,
  handleToggleButton?: (newValue: boolean) => void,
) => {
  const handleClick = (newValue: boolean) => {
    if (disabled) return;

    if (handleToggleButton) {
      handleToggleButton(newValue);
    } else if (setPlayerSummary) {
      setPlayerSummary(newValue);
    }
  };

  return (
    <div className="flex items-center gap-2 w-fit">
      <Button
        disabled={disabled}
        onClick={() => handleClick(true)}
        className={`px-4 py-[6px] rounded-full text-base font-semibold min-w-[120px]
          ${
            value
              ? activeYesClass
              : 'bg-white border border-gray-300 text-[#151740] hover:bg-white hover:text-[#151740]'
          } ${disabled ? 'pointer-events-none opacity-60' : ''}`}
      >
        <Trans i18nKey="common.yes" />
      </Button>

      <Button
        disabled={disabled}
        onClick={() => handleClick(false)}
        className={`px-4 py-[6px] rounded-full text-base font-semibold min-w-[120px]
          ${
            !value
              ? activeNoClass
              : 'bg-white border border-gray-300 text-[#151740] hover:bg-white hover:text-[#151740]'
          } ${disabled ? 'pointer-events-none opacity-60' : ''}`}
      >
        <Trans i18nKey="common.no" />
      </Button>
    </div>
  );
};

/**
 * Renders a small UI section for configuring "Consecutive Sweeps".
 *
 * Includes:
 * - A text input field where the user can enter the number of consecutive sweeps.
 * - A toggleable flag icon (active/inactive) to enable or disable the feature.
 *
 * Props:
 * @param playerData - Contains the current values for `consecutiveSweeps` and `consecutiveSweepsFlag`.
 * @param setPlayerSummary- A setter function to update the playerData state.
 *
 * Behavior:
 * - Updates `consecutiveSweeps` when the input changes.
 * - Toggles `consecutiveSweepsFlag` when the flag button is clicked.
 */

export const renderConsecutiveSweep = (
  playerData: PlayerUpdate,
  setPlayerSummary: (updated: PlayerUpdate) => void,
  isDisable?: boolean,
  isTablet?: boolean,
) => {
  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        placeholder="Enter"
        min="0"
        disabled={isDisable}
        value={playerData.consecutiveSweepsCount ?? ''}
        onChange={(e) =>
          setPlayerSummary({
            ...playerData,
            consecutiveSweepsCount:
              isUndefined(e.target.value) || e.target.value === ''
                ? undefined
                : Math.max(0, Number(e.target.value)),
          })
        }
        className="bg-[#F7F7F7] rounded-full px-4 py-2 text-sm w-24 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />

      {!isTablet && renderConsecutiveSweepFlag(playerData, setPlayerSummary)}
    </div>
  );
};

export const renderConsecutiveSweepFlag = (
  playerData: PlayerUpdate,
  setPlayerSummary: (updated: PlayerUpdate) => void,
) => {
  return (
    <button
      type="button"
      onClick={() =>
        setPlayerSummary({
          ...playerData,
          consecutiveSweepsFlag: !playerData.consecutiveSweepsFlag,
        })
      }
    >
      <img
        src={playerData.consecutiveSweepsFlag ? ActiveFlag : InactiveFlag}
        alt="flag"
        className="h-5 w-6 object-contain mt-1"
      />
    </button>
  );
};
